import {CBARObject3D, CBARObject3DProperties} from "./CBARObject3D";
import {CBARContext} from "../CBARContext";
import {CBARMaskTexture} from "./CBARMaskTexture";
import {CBARTextureType} from "./CBARMaterial";
import * as THREE from "three";
import {CBARHighlightState, CBARSurfaceType} from "../CBARTypes";
import {CBARImageCollection, CBARImageDictionary} from "./CBARImageCollection";
import {CBARSurfaceAsset} from "../assets";
import {Line2} from "three/examples/jsm/lines/Line2";
import {CBARImage} from "./CBARImage";
import {getIntensity, normalizedToScreen} from "../internal/Utils";
import {InputArray, Mat} from "mirada";
import {LineGeometry} from "three/examples/jsm/lines/LineGeometry";
import {LineMaterial} from "three/examples/jsm/lines/LineMaterial";

const SHOW_SURFACES = false;
const MAX_CONTOURS = 50;
const APPROX_POLY = true;
const POLY_EPSILON = 1.0;
const MAX_PROJECTED_DISTANCE = 100.0;
const MIN_CONTOUR_AREA = 20 * 20;
const NUM_ANIMATION_FRAMES = 5

export type Scalar = number[]

const colors = ['aqua', 'blue', 'red', 'green', 'lime', 'maroon', 'navy', 'olive',
    'purple', 'fuchsia', 'silver', 'teal', 'white', 'yellow', 'orange'];
let colorIndex = 0;

export interface CBARSurfaceProperties extends CBARObject3DProperties {
    type:CBARSurfaceType
    color?:string
    normal:Scalar
    offset:number
    axisRotation?:number
    maskIndex?:number
    images?:CBARImageDictionary
    backgroundMean?:Scalar
    backgroundStdDev?:Scalar
    lightingMean?:Scalar
    lightingStdDev?:Scalar
}

export class CBARSurface extends CBARObject3D<CBARSurface> {

    _generatedColor = new THREE.Color(colors[(colorIndex ++) % colors.length]);

    public constructor(context:CBARContext, public readonly maskIndex:number) {
        super(context);

        this._maskTexture = new CBARMaskTexture(this.context);
        this.material = new THREE.MeshLambertMaterial({color:this._generatedColor, transparent: true, opacity: this.baseOpacity, depthTest:false});
        this.setRenderObject(this._container);
    }

    protected _container = new THREE.Group();
    public baseOpacity = SHOW_SURFACES ? 0.5 : 0.0;
    public material:THREE.MeshLambertMaterial;

    protected images = new CBARImageCollection(this.context);
    private _outlines:Line2[] = [];
    private _shapeMeshes:THREE.Mesh[] = [];

    protected _assets: { [id: string]: CBARSurfaceAsset} = {};

    public get selectedColor() {
        return this.color
    }

    public get values() : { [id: string] : CBARSurfaceAsset; } {
        return this._assets
    }

    public first() {
        const length = this.length();
        if (length) {
            return this.all()[0];
        }
    }

    public last() {
        const length = this.length();
        if (length) {
            return this.all()[length-1];
        }
    }

    public all() {
        return Object.values(this._assets)
    }

    public get sorted() : CBARSurfaceAsset[] {
        return Object.values(this._assets).sort((a, b) => a.surfaceElevation - b.surfaceElevation)
    }

    private sortAssets() {
        const assets = this.sorted;//why is this changing?
        assets.forEach(asset=>{
            this.renderObject.remove(asset.renderObject);
        });
        assets.forEach(asset=>{
            this.renderObject.add(asset.renderObject);
        });

        this.needsUpdate();
    }

    public add(asset:CBARSurfaceAsset, elevation=0.0) {

        asset.surface = this;

        if (this.type === CBARSurfaceType.Floor) {
            //place in front of us on the floor, otherwise (default) the center of the surface
            const maskCenter = new THREE.Vector2(0.5,0.9);//hard coded center of floor in front and down.
            const center = this.screenToSurfacePosition(maskCenter);
            asset.setSurfacePosition(center.x, center.y);
        }

        asset.surfaceElevation = elevation;
        
        this._assets[asset.id] = asset;

        this.context.scene?.assets.add(asset);
        asset.addedToSurface(this);

        this.sortAssets();
    }

    public containsKey(key:string) : boolean {
        return this._assets.hasOwnProperty(key);
    }

    public remove(asset:CBARSurfaceAsset) {
        if (!this.containsKey(asset.id) || asset.surface !== this) return;
        this.context.scene?.assets.remove(asset);
        this.needsUpdate();
    }

    //internal: called from CBARSurfaceAsset.removeFromScene()
    private internal_removeKey(key:string) {
        delete this._assets[key]
        this.needsUpdate();
    }

    public length(): number {
        return Object.keys(this._assets).length
    }

    private _extent = new THREE.Vector2();

    public get extent() : THREE.Vector2 {
        return this._extent
    }

    public type = CBARSurfaceType.Other;

    public axisRotation = 0.0;

    private _raycaster = new THREE.Raycaster();

    public get color() {
        return this.material.color
    }

    public set color(value) {
        this.material.color = value
    }

    public _maskTexture:CBARMaskTexture;

    public get maskTexture() {
        return this.isSingleMask ? this._maskTexture : this.context.scene?.indexMaskTexture;
    }

    protected _isSingleMask = false;
    public get isSingleMask() {
        return this._isSingleMask;
    }

    protected readonly plane = new THREE.Plane();

    get planeNormal() {
        return this.plane.normal;
    }
    set planeNormal(value) {
        this.plane.normal = value;
        this.regenerateMeshes(true);
        this.needsUpdate();
    }

    get planeOffset() {
        return this.plane.constant;
    }
    set planeOffset(value) {
        this.plane.constant = value;
        this.regenerateMeshes(true);
        this.needsUpdate();
    }

    public get maskImage() : CBARImage | undefined {
        if (this.context.scene?.indexMask) {
            return this.context.scene.indexMask;
        }
        else if (this.images.containsKey("mask")) {
            return this.images.values['mask'] as CBARImage;
        }
    }

    get description() : string {
        return `${this.type} Surface ${this.maskIndex}`
    }

    public data() : any {
        const data:any = super.data();

        data.extent = [this.extent.x, this.extent.y];

        data.color = this.color.getHexString();

        return data
    }

    public get selected() {
        return this.context.scene?.surfaceFor(CBARHighlightState.Selected) === this;
    }

    public set selected(value:boolean) {
        if (this.context.scene) {
            if (value) {
                this.context.scene.setSurfaceFor(CBARHighlightState.Selected, this);
            } else {
                const selectedSurface = this.context.scene.surfaceFor(CBARHighlightState.Selected);
                if (selectedSurface === this) {
                    this.context.scene.setSurfaceFor(CBARHighlightState.Selected, undefined);
                }
            }
        } else {
            console.warn("selected: No scene");
        }
    }

    public clearAll() {
        const assets = this.values;

        for (let key in assets) {
            this.remove(assets[key])
        }
    }

    public existsAtPoint(coords:THREE.Vector2) : boolean {
        const mask = this.maskTexture;
        if (!mask?.canvas) {
            return false;
        }

        const ctx = mask.canvas.getContext("2d", {willReadFrequently:true});

        if (!ctx) return false;

        const xPos = coords.x * mask.canvas.width;
        const yPos = coords.y * mask.canvas.height;

        const pixel = ctx.getImageData(xPos, yPos, 1, 1);

        if (pixel.data.length === 0) return false

        return this.isSingleMask ? pixel.data[0] > 0 : pixel.data[0] === this.maskIndex;
    }

    protected getPlaneIntersection(point2D: THREE.Vector2) {
        const screenPoint = normalizedToScreen(point2D);
        this._raycaster.setFromCamera(screenPoint, this.context.gl.camera);
        return this._raycaster.ray.intersectPlane(this.plane, new THREE.Vector3());
    }

    public screenToSurfacePosition(point2D:THREE.Vector2) : THREE.Vector2 {
        const point3D = this.getPlaneIntersection(point2D);
        return point3D ? this.getSurfacePosition(point3D) : new THREE.Vector2();
    }

    private _pointTransform = new THREE.Matrix4().identity();

    public getSurfacePosition(point3d:THREE.Vector3) : THREE.Vector2 {
        const point4D = new THREE.Vector4(point3d.x, point3d.y, point3d.z, 1.0).applyMatrix4(this._pointTransform);
        return new THREE.Vector2(point4D.x, point4D.y);
    }

    private _menuPoint = new THREE.Vector2(0,0)
    public get menuPoint() {
        return this.context.pointToScreenPosition(this._menuPoint)
    }

    private _mask?:Mat;

    public get mask() {

        if (!this._mask) {

            if (this.context.scene?.cvIndexMask) {
                //if not equal to value, set to 0
                this._mask = new cv.Mat();
                cv.threshold(this.context.scene.cvIndexMask, this._mask, this.maskIndex, 0, cv.THRESH_TOZERO_INV); //if greater than threshold, set to zero
                cv.threshold(this._mask, this._mask, this.maskIndex - 1, 0, cv.THRESH_TOZERO); //if less than thresh, set to zero
            } else if (this.maskTexture?.canvas) {
                this._mask = cv.imread(this.maskTexture.canvas);
                if (this._mask.channels() == 4) {
                    cv.cvtColor(this._mask, this._mask, cv.COLOR_RGBA2GRAY, 0)
                } else if (this._mask.channels()==3) {
                    cv.cvtColor(this._mask, this._mask, cv.COLOR_RGB2GRAY, 0)
                }
                //mobile-only issue with artifacts for some unknown reason, causing contour counts to go into the thousands
                cv.threshold(this._mask, this._mask, 0, 255, cv.THRESH_TOZERO | cv.THRESH_OTSU);
            }

            this._mask?.setTo(cv.Scalar.all(255), this._mask)
        }

        return this._mask;
    }

    private calculateLighting(background:InputArray, lighting:InputArray, mask:InputArray) {
        console.log("Recalculating lighting");
        const maskSmall = new cv.Mat()
        cv.resize(mask, maskSmall, background.size(), 0,0, cv.INTER_NEAREST);

        let insideM = new cv.Mat();
        let insideS = new cv.Mat();
        cv.meanStdDev(background, insideM, insideS, maskSmall);

        this.backgroundMeanIntensity = getIntensity(insideM.data64F);
        this.backgroundStdDevIntensity = getIntensity(insideS.data64F);

        cv.meanStdDev(lighting, insideM, insideS, maskSmall);

        this.lightingMeanIntensity = getIntensity(insideM.data64F);
        this.lightingStdDevIntensity = getIntensity(insideS.data64F);

        insideM.delete();
        insideS.delete();
        maskSmall.delete();
    }

    protected findContours(calculateTransform:boolean) {

        if (!this.mask) {
            return;
        } //nothing to do yet.

        const contours = new cv.MatVector();
        const hierarchy = new cv.Mat();
        const pad = 1;
        const paddedMask = cv.Mat.zeros(this.mask.rows + 2 * pad, this.mask.cols + 2 * pad, cv.CV_8U);
        this.mask.copyTo(paddedMask.roi({x:pad,y:pad,width:this.mask.cols, height:this.mask.rows}))
        cv.findContours(paddedMask, contours, hierarchy, cv.RETR_EXTERNAL, cv.CHAIN_APPROX_SIMPLE);
        const contoursLength = (contours.size() as any) as number; //hack: typedefs are wrong inside mirada. regenerate using opencv please (it is incorporated now)
        hierarchy.delete();

        const threeContours: THREE.Vector2[][] = [];

        const menuPadding = 0.05
        this._menuPoint.x = menuPadding
        this._menuPoint.y = 1 - menuPadding

        console.log("Getting contours");

        for (let i = 0; i < Math.min(contoursLength, MAX_CONTOURS); i++) {
            const contour = contours.get(i);

            let poly = new cv.Mat();
            if (APPROX_POLY) {
                cv.approxPolyDP(contour, poly, POLY_EPSILON, false)
            } else {
                poly = contour
            }

            const area = cv.contourArea(poly);
            if (area < MIN_CONTOUR_AREA) continue;

            const threeContour:THREE.Vector2[] = [];
            for (let j = 0; j < poly.data32S.length; j+=2) {
                const point = new THREE.Vector2((poly.data32S[j]-pad)/this.mask?.cols, (poly.data32S[j+1]-pad)/this.mask?.rows);
                threeContour.push(point);

                if (point.y < this._menuPoint.y && point.x > this._menuPoint.x) {
                    this._menuPoint = point
                }
            }
            threeContours.push(threeContour);
            poly.delete();
        }
        
        contours.delete();

        this.maskTexture?.refresh();

        this.removeMeshes();

        this._menuPoint.x = Math.min(Math.max(this._menuPoint.x, menuPadding), 1-menuPadding);
        this._menuPoint.y = Math.min(Math.max(this._menuPoint.y, menuPadding), 1-menuPadding);

        const mx = new THREE.Matrix4().lookAt(this.plane.normal, new THREE.Vector3(), new THREE.Vector3(0,1,0));
        const _rotation = new THREE.Quaternion().setFromRotationMatrix(mx);

        const euler = new THREE.Euler().setFromQuaternion(_rotation);
        const rotation = new THREE.Quaternion().setFromEuler(new THREE.Euler(euler.x, euler.y, this.axisRotation));

        this.castContours(threeContours, rotation, calculateTransform);

        this._container.rotation.setFromQuaternion(rotation);
    }

    public lightingMeanIntensity = 0;
    public lightingStdDevIntensity = 0;

    public backgroundMeanIntensity = 0;
    public backgroundStdDevIntensity = 0;

    private projectPointsOntoPlane(contours: THREE.Vector2[][], contours3D:THREE.Vector3[][]) {
        const totalPoint = new THREE.Vector3();
        let numPoints = 0;

        for (let i = 0; i < contours.length; i++) {
            const contour = contours[i];
            const points3D: THREE.Vector3[] = [];
            for (let j = 0; j < contour.length; j++) {

                const point3D = this.getPlaneIntersection(contour[j]);

                if (point3D && point3D.length() <= MAX_PROJECTED_DISTANCE) {
                    points3D.push(point3D);
                    totalPoint.x += point3D.x;
                    totalPoint.y += point3D.y;
                    totalPoint.z += point3D.z;
                    numPoints += 1
                }
            }

            if (points3D.length > 2) {
                points3D.push(points3D[0]);//join start and end
                contours3D.push(points3D);
            }
        }

        return new THREE.Vector3(
            totalPoint.x / numPoints,
            totalPoint.y / numPoints,
            totalPoint.z / numPoints);
    }

    private castContours(contours: THREE.Vector2[][], rotation:THREE.Quaternion, calculateTransform:boolean) {

        const contours3D:THREE.Vector3[][] = [];
        const center3D = this.projectPointsOntoPlane(contours, contours3D);

        if (calculateTransform) {
            this._pointTransform = new THREE.Matrix4().compose(center3D, rotation, new THREE.Vector3(1,1,1)).invert()
        }

        const min = new THREE.Vector2(Number.MAX_SAFE_INTEGER,Number.MAX_SAFE_INTEGER);
        const max = new THREE.Vector2(Number.MIN_SAFE_INTEGER,Number.MIN_SAFE_INTEGER);
        const contoursPlane = [];

        let index = 0;

        for (const contour of contours3D) {
            const translatedContour = [];

            for (const point3D of contour) {
                const point2D = this.getSurfacePosition(point3D);
                translatedContour.push(point2D);
                min.x = Math.min(min.x, point2D.x);
                min.y = Math.min(min.y, point2D.y);
                max.x = Math.max(max.x, point2D.x);
                max.y = Math.max(max.y, point2D.y);
            }
            translatedContour.push(translatedContour[0]); //close loop

            const shape = new THREE.Shape();
            shape.setFromPoints(translatedContour);
            contoursPlane.push(shape);

            //add outside lines
            const lineMaterial = new LineMaterial({
                vertexColors: false,
                transparent:true,
                opacity: 0.0,
                color:new THREE.Color("blue").getHex(),
                linewidth: 8.0,
                blending:THREE.AdditiveBlending,
                resolution: new THREE.Vector2(this.context.gl.renderer.domElement.width * window.devicePixelRatio, this.context.gl.renderer.domElement.height * window.devicePixelRatio),
                dashed: false,
                depthTest:false
            });

            const positions = [];
            const colors = [];
            for (const point of translatedContour) {
                positions.push(point.x, point.y, 0);
                colors.push( this.color.r, this.color.g, this.color.b )
            }

            const lineGeometry = new LineGeometry();
            lineGeometry.setPositions(positions);
            lineGeometry.setColors(colors);

            const line = new Line2(lineGeometry, lineMaterial );
            line.computeLineDistances();
            line.scale.set( 1, 1, 1 );
            line.visible = true;
            line.renderOrder = 10000;

            this._outlines.push(line);
            this._container.add(line);

            index += 1;
        }

        //add each shape
        for (const shape of contoursPlane) {
            //add shape
            const geometry = new THREE.ShapeBufferGeometry(shape);
            const mesh = new THREE.Mesh(geometry, this.material);
            mesh.renderOrder = 10000;
            this._container.add(mesh);
            this._shapeMeshes.push(mesh);
        }

        //calc extents
        const xSpan = 2 * Math.max(max.x - min.x, 2);
        const ySpan = 2 * Math.max(max.y - min.y, 2);

        this._extent = new THREE.Vector2(xSpan, ySpan);
        if (calculateTransform) {
            this._container.position.set(center3D.x, center3D.y, center3D.z);
        }
    }

    private get hasCalculatedLighting() {
        return this.backgroundMeanIntensity && this.backgroundStdDevIntensity && this.lightingMeanIntensity && this.lightingStdDevIntensity;
    }

    protected regenerateMeshes(calculateTransform:boolean) {

        this.findContours(calculateTransform);

        const background = this.context.scene?.cvBackgroundSmall;
        const lighting = this.context.scene?.cvLightingSmall;

        if (!this.hasCalculatedLighting && background && lighting && this.mask) {
            this.calculateLighting(background, lighting, this.mask)
        }

        this.sortAssets();
    }

    private _highlighted = false;
    private _finalOpacityValue = 0;

    public set highlighted(value:boolean) {
        if (this._highlighted == value) return;

        this._highlighted = value;
        this._finalOpacityValue = value ? 1.0 : 0;

        this._outlines.forEach(o=>{
            o.visible = true;
            o.material.opacity = value ? 0 : 1;
            o.material.needsUpdate = true;
        })

        const duration = 1000 * NUM_ANIMATION_FRAMES / 30;
        this.context.scene?.requestAnimation(duration).then(()=>{
            this._outlines.forEach(o=>{
                o.visible = value;
                o.material.opacity = this._finalOpacityValue;
                o.material.needsUpdate = true;
            })
        })
    }

    public get highlighted() {
        return this._highlighted;
    }

    removeFromScene() : void {
        this.removeMeshes();
        super.removeFromScene();
    }

    sceneLoaded() {
        this.regenerateMeshes(true);
        this.needsUpdate();
    }

    protected removeMeshes() {

        this._outlines.forEach(m=>this._container.remove(m));
        this._outlines = [];

        this._shapeMeshes.forEach(m=>this._container.remove(m));
        this._shapeMeshes = [];
    }

    public get receivesEvents() : boolean {
        return true
    }

    public get hasOutlines() {
        return this.all().length === 0;
    }
}

